import React from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import Button from "shared/Button";
import Loader from "shared/Loader";
import { promotions as promotionsApi } from "shared/api";
import { formatShortDate } from "shared/@utils/formatDate";
import PanelHeader from "../components/PanelHeader";
import styles from "./PromotionEditor.module.css";

const STATUS_LABELS = {
  archived: "Archived",
  in_review: "In Review",
  scheduled: "Scheduled",
  published: "Published",
  draft: "Draft",
};

const formatDate = (value) => formatShortDate(value, "—");

/**
 * Promotion editor (placeholder).
 *
 * The promotions feature and its editor design are still being defined (see
 * meeting notes), so this page is intentionally read-only / unhooked for now:
 * it loads the selected promotion and lays out the fields we already know about
 * so the route exists and the shape is visible. Inputs are disabled until the
 * data contract and design land.
 */
export default function PromotionEditor() {
  const { promotionId } = useParams();
  const history = useHistory();
  const { url } = useRouteMatch();
  const [promotion, setPromotion] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // Route is ".../:promotionId/edit" — strip the trailing segments to get back
  // to the list.
  const listUrl = url.replace(/\/[^/]+\/edit$/, "");

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    promotionsApi
      .get(promotionId)
      .then((p) => {
        if (!cancelled) setPromotion(p);
      })
      .catch(() => {
        // Surface as the "not found" state rather than leaving the rejection
        // unhandled; the editor is read-only so there is nothing else to retry.
        if (!cancelled) setPromotion(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [promotionId]);

  const handleBack = () => history.push(listUrl);

  return (
    <div className={styles.editor}>
      <PanelHeader
        title={`WWG Store - ${promotion ? promotion.name : "Promotion"}`}
      />

      {loading ? (
        <div className={styles.loaderWrap}>
          <Loader size={60} />
        </div>
      ) : !promotion ? (
        <div className={styles.notFound}>
          <p>That promotion could not be found.</p>
          <Button onClick={handleBack} title="Return to the promotions list.">
            Back to Promotions
          </Button>
        </div>
      ) : (
        <div className={styles.body}>
          <div className={styles.notice}>
            The promotions editor is still being designed. This is a read-only
            preview of what we know about a promotion so far.
          </div>

          <div className={styles.grid}>
            <label className={styles.field}>
              <span className={styles.label}>Title</span>
              <input
                className={styles.input}
                value={promotion.name || ""}
                disabled
                readOnly
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Type</span>
              <input
                className={styles.input}
                value={promotion.type || ""}
                disabled
                readOnly
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Status</span>
              <input
                className={styles.input}
                value={
                  STATUS_LABELS[promotion.status] || promotion.status || ""
                }
                disabled
                readOnly
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Starts</span>
              <input
                className={styles.input}
                value={formatDate(promotion.startsAt)}
                disabled
                readOnly
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Ends</span>
              <input
                className={styles.input}
                value={formatDate(promotion.endsAt)}
                disabled
                readOnly
              />
            </label>

            <label className={`${styles.field} ${styles.fieldWide}`}>
              <span className={styles.label}>Summary</span>
              <textarea
                className={styles.textarea}
                value={promotion.summary || ""}
                disabled
                readOnly
              />
            </label>
          </div>

          <div className={styles.actions}>
            <Button onClick={handleBack} title="Return to the promotions list.">
              Back to Promotions
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
